import { useSnackbar } from "notistack";
import {
  Typography,
  TableCell,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Box,
} from "@mui/material";
import { Loading, LoadingFailed } from "~/components/loading";
import { LoadMore } from "~/components/load-more";
import { FormatDate } from "~/lib/date";
import { NoPermission } from "~/components/no-permission";
import {
  AuditLogForwardPaginationQueryVariables,
  AuditLogOrderField,
  OrderDirection,
  useAuditLogForwardPaginationQuery,
} from "~/operations";
import { useAuditLogFilterContext } from "./useAuditlogFilterContext";
import { useCanListLogs } from "~/lib/iam/actions/mondoo/auditlog/AuditLogManager/ListLogs";

export type AuditLogProps = {
  resourceMrn: string;
};

export function AuditLog({ resourceMrn }: AuditLogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const pageSize = 25;

  const hasListLogEntriesPermission = useCanListLogs();
  const { filterForm, filterVariables } = useAuditLogFilterContext({
    scopeMrn: resourceMrn,
  });

  const variables: AuditLogForwardPaginationQueryVariables = {
    resourceMrn,
    ...filterVariables,
    orderBy: {
      field: AuditLogOrderField.Timestamp,
      direction: OrderDirection.Desc,
    },
    first: pageSize,
  };

  const { loading, data, error, fetchMore } = useAuditLogForwardPaginationQuery(
    {
      variables: { ...variables },
      skip: !hasListLogEntriesPermission,
    },
  );

  const auditLog =
    data?.auditlog?.edges?.flatMap((edge) => edge.node ?? []) || [];
  const hasNextPage = data?.auditlog?.pageInfo.hasNextPage;
  const endCursor = data?.auditlog?.pageInfo.endCursor;

  const loadMore = async () => {
    try {
      await fetchMore({
        variables: {
          after: endCursor,
          first: pageSize,
        },
      });
    } catch (error) {
      enqueueSnackbar("Failed to load auditlog", {
        variant: "error",
      });
    }
  };

  if (!hasListLogEntriesPermission) {
    return <NoPermission />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {filterForm}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Subject</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Loading what="Audit Log" />
                </TableCell>
              </TableRow>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={3}>
                  <LoadingFailed what="Audit Log" />
                </TableCell>
              </TableRow>
            )}
            {!loading && auditLog.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No audit log entries found</TableCell>
              </TableRow>
            )}
            {!loading &&
              auditLog.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography variant="body1">
                      {item.identity.name || item.identity.mrn}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography variant="body1">{item.msg}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {item.action}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {item.resource}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    {FormatDate(item.timestamp)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && auditLog.length > 0 && hasNextPage && (
        <LoadMore
          handleLoadMore={loadMore}
          hasNextPage={hasNextPage}
          loading={loading}
        />
      )}
    </Box>
  );
}
